<template>
    <div class="card mt-2">
        <div class="card-header d-flex justify-content-between align-items-start">
            <h5 class="card-title">Turns</h5>
            <span>{{ myTotals }}</span>
            <span v-if="permissions.canViewAllDetails">{{ allTotals }}</span>
            <b-button-group>
                <b-button size="sm"
                          variant="outline-secondary"
                          v-on:click="reload"
                          v-if="!readonly && !turnsReadonly">
                    <i class="fas fa-sync mr-1"></i>Reload
                </b-button>
                <b-dropdown size="sm"
                            variant="outline-secondary"
                            right
                            v-if="(!readonly && !turnsReadonly && permissions.canManageTurns) || addTurnZeroActionAvailable || clearUserPreAssignmentAvailable || permissions.canViewAllDetails || downloadTurnsForQCActionAvailable">
                    <template v-slot:button-content>
                        Turn Actions
                    </template>
                    <b-dropdown-item v-on:click="openAddTurnTranscriptWorkItemsDialog"
                                     v-if="!readonly && !turnsReadonly && permissions.canManageTurns">
                        <b-iconstack font-scale="1.5" class="mr-2">
                            <b-icon stacked icon="file-earmark-plus" variant="success" scale="1"></b-icon>
                        </b-iconstack>
                        <span class="text-success">Add</span>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="trimTurnTranscriptWorkItems"
                                     v-if="!readonly && !turnsReadonly && permissions.canManageTurns">
                        <b-iconstack font-scale="1.5" class="mr-2">
                            <b-icon stacked icon="file-earmark" variant="danger" scale="1"></b-icon>
                            <b-icon stacked icon="trash" variant="danger" scale="0.6" shift-v="-2"></b-icon>
                        </b-iconstack>
                        <span class="text-danger">Trim</span>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="openBlankAudioModal"
                                     v-if="!readonly && !turnsReadonly && permissions.canManageTurns">
                        <div class="text-secondary">Manage blank audio segments</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="openTurnZeroModal"
                                     v-if="addTurnZeroActionAvailable">
                        <div class="text-secondary">Add turn 0</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="showClearUserPreAssignment"
                                     v-if="clearUserPreAssignmentAvailable">
                        <div class="text-danger"><i class="fas fa-user-alt-slash mr-1"></i>Clear Pre-Assignments</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="openFirstAndLastWordModal = true"
                                     v-if="permissions.canViewAllDetails">
                        <div class="text-secondary">View First and Last Words</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="openViewAllCommentsModal = true"
                                     v-if="permissions.canViewAllDetails">
                        <div class="text-secondary">View all comments</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="showSelectTurnAttachments(false)"
                                     v-if="permissions.canViewAllDetails && !hasTopscribeDocuments">
                        <div class="text-secondary">Download turns for merging</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="showSelectTurnAttachments(true)"
                                     v-if="downloadTurnsForQCActionAvailable">
                        <div class="text-secondary">Download turns for QC</div>
                    </b-dropdown-item>
                    <b-dropdown-item v-on:click="mergeAllAvailableTurns"
                                     v-if="mergeAllAvailableTurnsAvailable">
                        <div class="text-secondary">Merge All Available Turns</div>
                    </b-dropdown-item>
                </b-dropdown>
            </b-button-group>
        </div>
        <ul class="list-group list-group-flush" v-if="turnTranscriptWorkItems.length == 0">
            <li class="list-group-item">
                The list is currently empty
            </li>
        </ul>

        <b-table :items="mergedTurnBlankAudioSegments" :fields="tableFields" v-if="mergedTurnBlankAudioSegments.length > 0"
                 :tbody-tr-class="rowClass">
            <template v-slot:cell(info)="row">
                <template v-if="row.item.completedDateTimeUTC">
                    <div><span class="font-weight-bold">at:</span><span class="ml-1">{{ formatDateTime(row.item.completedDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span></div>
                    <div><span class="font-weight-bold">by:</span><span class="ml-1">{{ row.item.completedByUserAccountDisplayName }}</span></div>
                    <div v-if="row.item.numberOfWords && canViewTurnDetails(row.item)">
                        <span class="font-weight-bold">with:</span>
                        <span class="ml-1">
                            {{ row.item.numberOfWords }} words / {{ getNumberOfFolios(row.item.numberOfWords) }} folios
                            <template v-if="row.item.numberOfPages">
                                on {{ row.item.numberOfPages }} pages
                            </template>
                        </span>
                    </div>
                    <div v-if="row.item.numberOfAudioSeconds && canViewTurnDetails(row.item)">
                        <span class="font-weight-bold">for:</span>
                        <span class="ml-1">
                            {{ formatSecondsAsDuration(row.item.numberOfAudioSeconds) }}
                        </span>
                    </div>
                    <div v-if="row.item.isEmptyTurn">
                        <span class="font-weight-bold">Marked Empty</span>
                    </div>
                </template>
                <template v-if="row.item.inProgressSinceDateTimeUTC">
                    <div><span class="font-weight-bold">since:</span><span class="ml-1">{{ formatDateTime(row.item.inProgressSinceDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span></div>
                    <div><span class="font-weight-bold">by:</span><span class="ml-1">{{ row.item.inProgressByUserAccountDisplayName }}</span></div>
                </template>
                <template v-if="row.item.preAssignedUserAccountId">
                    <div><span class="font-weight-bold">pre-assigned to:</span><span class="ml-1">{{ row.item.preAssignedUserAccountDisplayName }}</span></div>
                </template>

                <div v-if="row.item.firstWords || row.item.inProgressByUserAccountId">
                    <span class="font-weight-bold">first words:</span>
                    <!-- <b-badge class="ml-1" href="#" variant="secondary" v-on:click="openCommentsDialog(row.item.comments)">view</b-badge> -->
                    <b-badge v-if="row.item.firstWords" :ref="'firstwordsbutton' + row.item.turnNumberDisplayText" :id="'firstwordsbutton' + row.item.turnNumberDisplayText" class="ml-2" size="sm">view</b-badge>
                    <b-tooltip v-if="row.item.firstWords" :target="'firstwordsbutton' + row.item.turnNumberDisplayText" triggers="hover" variant="info" solid="true" class="p-2">
                        {{ row.item.firstWords }}
                    </b-tooltip>
                    <b-link v-if="row.item.firstWords" v-on:click="copyToClipboard(row.item.firstWords)" class="ml-2">Copy</b-link>
                    <b-link v-if="(row.item.inProgressByUserAccountId || row.item.firstWords) && editFirstLastWordActionAvailable(row.item)" v-on:click="openFirstLastWordEdit(row.item)" class="ml-2">Edit</b-link>
                </div>

                <div v-if="row.item.lastWords">
                    <span class="font-weight-bold">last words:</span>
                    <!-- <b-badge class="ml-1" href="#" variant="secondary" v-on:click="openCommentsDialog(row.item.comments)">view</b-badge> -->
                    <b-badge :ref="'lastwordsbutton' + row.item.turnNumberDisplayText" :id="'lastwordsbutton' + row.item.turnNumberDisplayText" class="ml-2" size="sm">view</b-badge>
                    <b-tooltip :target="'lastwordsbutton' + row.item.turnNumberDisplayText" triggers="hover" variant="info" solid="true" class="p-2">
                        {{ row.item.lastWords }}
                    </b-tooltip>
                    <b-link v-on:click="copyToClipboard(row.item.lastWords)" class="ml-2">Copy</b-link>
                    <b-link v-if="editFirstLastWordActionAvailable(row.item)" v-on:click="openFirstLastWordEdit(row.item)" class="ml-2">Edit</b-link>
                </div>

                <div v-if="row.item.comments">
                    <span class="font-weight-bold">with comments</span>
                    <!-- <b-badge class="ml-1" href="#" variant="secondary" v-on:click="openCommentsDialog(row.item.comments)">view</b-badge> -->
                    <b-badge :ref="'commentsbutton' + row.item.turnNumberDisplayText" :id="'commentsbutton' + row.item.turnNumberDisplayText" class="ml-2" size="sm">view</b-badge>
                    <b-tooltip :target="'commentsbutton' + row.item.turnNumberDisplayText" triggers="hover" variant="info" solid="true" class="p-2">
                        {{ row.item.comments }}
                    </b-tooltip>
                </div>

                <template v-if="canViewTurnAttachments(row.item)">
                    <div>
                        <span class="font-weight-bold">{{ isUuidNullOrEmpty(row.item.turnTranscriptFileId) ? 'without Transcript' : 'with Transcript' }}</span>

                        <b-link v-on:click="markTurnTranscriptFileAsDownloaded(row.item.turnTranscriptFileId)"
                                :href="$urlHelper.getFileDownloadUrl(row.item.turnTranscriptFileId)"
                                v-if="!isUuidNullOrEmpty(row.item.turnTranscriptFileId)"
                                v-bind:class="turnTranscriptFileWasDownloadedPreviously(row.item.turnTranscriptFileId) ? 'file-already-downloaded' : ''"
                                class="ml-2">Download</b-link>

                        <b-link v-on:click="openAttachmentDialog(row.item)" class="ml-2" v-if="canModifyTurnAttachment(row.item)">Modify</b-link>
                    </div>
                </template>

                <template v-if="row.item.mergedDateTimeUTC">
                    <div>
                        <span class="font-weight-bold">Merged at:</span><span class="ml-1">{{ formatDateTime(row.item.mergedDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span>
                        <span class="font-weight-bold ml-1">by:</span><span class="ml-1">{{ row.item.mergedByUserAccountDisplayName }}</span>
                    </div>
                </template>


                <template v-if="row.item.preAssignedQCUserAccountId && !row.item.qcInProgressByUserAccountId && !row.item.qcDateTimeUTC">
                    <div><span class="font-weight-bold">QC pre-assigned to:</span><span class="ml-1">{{ row.item.preAssignedQCUserAccountDisplayName }}</span></div>
                </template>

                <template v-if="row.item.qcInProgressByUserAccountId && !row.item.qcDateTimeUTC">
                    <div><span class="font-weight-bold">QC in progress by:</span><span class="ml-1">{{ row.item.qcInProgressByUserAccountDisplayName }}</span></div>
                </template>

                <template v-if="row.item.qcDateTimeUTC">
                    <div>
                        <span class="font-weight-bold">QC checked at:</span><span class="ml-1">{{ formatDateTime(row.item.qcDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span>
                        <span class="font-weight-bold ml-1">by:</span><span class="ml-1">{{ row.item.qcByUserAccountDisplayName }}</span>
                    </div>
                    <div v-if="canViewQcDetails(row.item)">
                        <span class="font-weight-bold">errors:</span><span class="ml-1">{{ row.item.qcErrorCount }}</span>
                    </div>
                </template>

                <template v-if="canViewTurnAttachments(row.item, true)">
                    <div>
                        <span class="font-weight-bold">{{ isUuidNullOrEmpty(row.item.correctedTurnTranscriptFileId) ? 'without Corrected Transcript' : 'with Corrected Transcript' }}</span>

                        <b-link v-on:click="markTurnTranscriptFileAsDownloaded(row.item.correctedTurnTranscriptFileId)"
                                :href="$urlHelper.getFileDownloadUrl(row.item.correctedTurnTranscriptFileId)"
                                v-if="!isUuidNullOrEmpty(row.item.correctedTurnTranscriptFileId)"
                                v-bind:class="turnTranscriptFileWasDownloadedPreviously(row.item.correctedTurnTranscriptFileId) ? 'file-already-downloaded' : ''"
                                class="ml-2">Download</b-link>

                        <b-link v-on:click="openAttachmentDialog(row.item)" class="ml-2" v-if="canModifyTurnAttachment(row.item, true)">Modify</b-link>
                    </div>
                </template>

            </template>
            <template v-slot:cell(actions)="row">
                <b-button-group v-if="!row.item.blankAudioSegmentId">
                    <b-button size="sm"
                              title="Start Turn"
                              variant="outline-secondary"
                              key="startwork"
                              v-on:click="startWork(row.item)"
                              v-if="startWorkActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="play-fill" variant="success" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Revert Turn"
                              variant="outline-secondary"
                              key="endwork"
                              v-on:click="endWork(row.item)"
                              v-if="endWorkActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="slash-lg" variant="danger" scale=".9"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="danger" scale=".9" rotate="90"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Complete"
                              variant="outline-secondary"
                              key="complete"
                              v-on:click="openCompleteTurnTranscriptWorkItemDialog(row.item)"
                              v-if="completeTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="check-lg" variant="success" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Open Editor"
                              variant="outline-secondary"
                              key="openeditor"
                              v-on:click="openEditor(row.item)"
                              v-if="openEditorActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="play-fill" variant="info" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="View Document"
                              variant="outline-secondary"
                              key="viewdocument"
                              :href="topscribeViewUrl(row.item)"
                              target="_blank"
                              v-if="viewDocumentActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="binoculars-fill" variant="info" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Mark as Empty"
                              variant="outline-secondary"
                              key="markempty"
                              v-on:click="openMarkEmptyTurnTranscriptWorkItemDialog(row.item)"
                              v-if="markEmptyTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="circle" variant="success" scale=".7"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="success" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Undo Mark as Empty"
                              variant="outline-secondary"
                              key="revertempty"
                              v-on:click="revertMarkEmptyTurnTranscriptWorkItem(row.item)"
                              v-if="revertEmptyTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="circle" variant="success" scale=".7"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="success" scale="1"></b-icon>
                            <b-icon stacked icon="arrow-counterclockwise" variant="danger" scale="1.3"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Start QC"
                              variant="outline-secondary"
                              key="startqc"
                              v-on:click="startQc(row.item)"
                              v-if="startQcActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="award" variant="dark" scale="1.1"></b-icon>
                            <b-icon stacked icon="play-fill" variant="success" scale="1" shift-h="0.8" shift-v="2.2"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Revert QC"
                              variant="outline-secondary"
                              key="endqc"
                              v-on:click="endQc(row.item)"
                              v-if="endQcActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="award" variant="dark" scale="1.1"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="danger" scale=".6" shift-v="2"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="danger" scale=".6" rotate="90" shift-v="2"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              :title="!row.item.qcDateTimeUTC ? 'Complete QC' : 'Update QC'"
                              variant="outline-secondary"
                              key="qcCheck"
                              v-on:click="openQcCheckDialog(row.item)"
                              v-if="qcCheckTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="award" variant="dark" scale="1.1"></b-icon>
                            <b-icon stacked icon="check-lg" variant="success" scale=".5" shift-h="0" shift-v="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Split Turn"
                              variant="outline-secondary"
                              key="split"
                              v-on:click="openSplitTurnDialog(row.item)"
                              v-if="splitTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="scissors" variant="info" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Undo Split Turn"
                              variant="outline-secondary"
                              key="undosplit"
                              v-on:click="undoSplitTurn(row.item)"
                              v-if="undoSplitTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="scissors" variant="info" scale="0.7"></b-icon>
                            <b-icon stacked icon="arrow-counterclockwise" variant="danger" scale="1.3"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Edit Totals"
                              variant="outline-secondary"
                              key="edittotals"
                              v-on:click="openEditTotalsDialog(row.item)"
                              v-if="editTotalsActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="pencil-fill" variant="info" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Merge"
                              variant="outline-secondary"
                              key="mergeturn"
                              v-on:click="mergeTurn(row.item)"
                              v-if="mergeTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="file-earmark-fill" variant="info" scale="0.8" shift-h="3" shift-v="-2"></b-icon>
                            <b-icon stacked icon="file-earmark" variant="info" scale="0.8" shift-h="-1" shift-v="3"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Remove"
                              variant="outline-secondary"
                              key="remove"
                              v-on:click="removeTurnTranscriptWorkItem(row.item)"
                              v-if="removeTurnActionAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="trash" variant="danger" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Create Pre-Assignment"
                              variant="outline-secondary"
                              key="createpreassignment"
                              v-on:click="showCreatePreAssignment(row.item)"
                              v-if="createPreAssignmentAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="person-fill" variant="info" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Clear Pre-Assignment"
                              variant="outline-secondary"
                              key="clearpreassignment"
                              v-on:click="clearPreAssignment(row.item)"
                              v-if="clearPreAssignmentAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="person-fill" variant="info" scale="1"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="danger" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Create QC Pre-Assignment"
                              variant="outline-secondary"
                              key="qcpreassignment"
                              v-on:click="showQcPreAssignment(row.item)"
                              v-if="createQcPreAssignmentAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="award" variant="dark" scale="1.1"></b-icon>
                            <b-icon stacked icon="person-fill" variant="info" scale=".6" shift-h="0" shift-v="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                    <b-button size="sm"
                              title="Clear QC Pre-Assignment"
                              variant="outline-secondary"
                              key="clearqcpreassignment"
                              v-on:click="showClearQcPreAssignment(row.item)"
                              v-if="clearQcPreAssignmentAvailable(row.item)">
                        <b-iconstack font-scale="1.5">
                            <b-icon stacked icon="award" variant="dark" scale="1.1"></b-icon>
                            <b-icon stacked icon="person-fill" variant="info" scale=".6" shift-h="0" shift-v="1"></b-icon>
                            <b-icon stacked icon="slash-lg" variant="danger" scale="1"></b-icon>
                        </b-iconstack>
                    </b-button>
                </b-button-group>
            </template>
            <template v-slot:custom-foot v-if="hasTopscribeDocuments && transcriptWorkItem">
                <b-tr>
                    <b-th colspan="3">Merged Turns</b-th>
                    <b-th>Status</b-th>
                    <b-th>Info</b-th>
                    <b-th>Actions</b-th>
                </b-tr>
                <b-tr>
                    <b-td colspan="3" class="fit">{{ transcriptWorkItem.mergedTurns }}</b-td>
                    <b-td>{{ transcriptWorkItem.ownStatusDisplayName }}</b-td>
                    <b-td>
                        <template v-if="transcriptWorkItem.completedDateTimeUTC">
                            <div><span class="font-weight-bold">at:</span><span class="ml-1">{{ formatDateTime(transcriptWorkItem.completedDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span></div>
                            <div><span class="font-weight-bold">by:</span><span class="ml-1">{{ transcriptWorkItem.completedByUserAccountDisplayName }}</span></div>
                        </template>
                        <template v-if="transcriptWorkItem.inProgressSinceDateTimeUTC">
                            <div><span class="font-weight-bold">since:</span><span class="ml-1">{{ formatDateTime(transcriptWorkItem.inProgressSinceDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span></div>
                            <div><span class="font-weight-bold">by:</span><span class="ml-1">{{ transcriptWorkItem.inProgressByUserAccountDisplayName }}</span></div>
                        </template>
                        <template v-if="transcriptWorkItem.qcDateTimeUTC">
                            <div class="small">
                                <span class="font-weight-bold">QC Checked at:</span><span class="ml-1">{{ formatDateTime(transcriptWorkItem.qcDateTimeUTC, 'DD/MM/YYYY h:mm a') }}</span>
                                <span class="font-weight-bold ml-1"> by:</span><span class="ml-1">{{ transcriptWorkItem.qcByUserAccountDisplayName }}</span>
                            </div>
                        </template>
                    </b-td>
                    <b-td class="fit">
                        <b-button-group>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="startwork"
                                      v-on:click="startWork(transcriptWorkItem)"
                                      v-if="startWorkActionAvailable(transcriptWorkItem)">
                                <b-icon class="mr-1" icon="play-fill" variant="success" scale="1"></b-icon>
                                Start Work
                            </b-button>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="openeditor"
                                      v-on:click="openEditor(transcriptWorkItem)"
                                      target="_blank"
                                      v-if="openEditorActionAvailable(transcriptWorkItem)">
                                <b-icon class="mr-1" icon="play-fill" variant="info" scale="1"></b-icon>
                                Open Editor
                            </b-button>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="viewdocument"
                                      :href="topscribeViewUrl(transcriptWorkItem)"
                                      target="_blank"
                                      v-if="viewDocumentActionAvailable(transcriptWorkItem)">
                                <b-icon class="mr-1" icon="binoculars-fill" variant="info" scale="1"></b-icon>
                                View Document
                            </b-button>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="endwork"
                                      v-on:click="endWork(transcriptWorkItem)"
                                      v-if="endWorkActionAvailable(transcriptWorkItem)">
                                <b-icon class="mr-1" icon="stop-fill" variant="danger" scale="1"></b-icon>
                                End Work
                            </b-button>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="productiondocuments"
                                      v-on:click="openProductionDocumentsDialog"
                                      v-if="openProductionDocumentsDialogActionAvailable()">
                                <b-icon class="mr-1" icon="gear-fill" variant="info" scale="1"></b-icon>
                                Production Documents
                            </b-button>
                            <b-button size="sm"
                                      variant="outline-secondary"
                                      key="fulfillments"
                                      v-on:click="openFulfillmentsDialog"
                                      v-if="openFulfillmentsDialogActionAvailable()">
                                <i class="fas fa-paper-plane text-info mr-1"></i>
                                Fulfillments
                            </b-button>
                        </b-button-group>
                    </b-td>
                </b-tr>
            </template>
        </b-table>

        <data-table-modal title="Turn Attachments"
                          ref="selectTurnAttachments"
                          v-model="openSelectTurnAttachmentsModal"
                          selectable
                          selectableKey="workItemId"
                          :unselectedKeys="choosePreviousSelectedKeys"
                          :tableData="turnAttachments"
                          :columns="allAttachmentsColumns"
                          @ok="okAllAttachmentsModal"
                          filterByField="turnNumber">
        </data-table-modal>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="md"
                     centered
                     title="Add Turn/s"
                     v-model="addTurnTranscriptWorkItemsDialogOpen"
                     v-on:ok.prevent="passes(addTurnTranscriptWorkItems)"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <validation-provider rules="required" name="Turn Length" v-slot="{ errors }" slim>
                        <b-form-group label="Turn Length"
                                      label-for="additionalTurnsLength"
                                      description="in minutes"
                                      class="col-md-6">
                            <b-form-input name="additionalTurnsLength"
                                          type="number"
                                          min="5"
                                          max="30"
                                          v-model="additionalTurnsLength"
                                          :state="errors[0] ? false : null"
                                          autofocus>
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="Number of Turns" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Number of Turns"
                                      label-for="additionalTurns"
                                      class="col-md-6">
                            <b-form-input name="additionalTurns"
                                          type="number"
                                          step="1"
                                          v-model="additionalTurns"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>
        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     title="Complete Turn"
                     v-model="completeTurnTranscriptWorkItemDialogOpen"
                     v-on:shown="completeTurnTranscriptWorkItemDialogShown"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:ok.prevent="passes(completeTurnTranscriptWorkItem)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Turn Number"
                                  label-for="turnNumber"
                                  class="col">
                        <b-form-input name="turnNumber"
                                      :value="currentTurnTranscriptWorkItem.turnNumberDisplayText"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="{ valid, errors }" slim
                                         v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enableWordCount">
                        <b-form-group label="Number of Words"
                                      label-for="numberOfWords"
                                      class="col">
                            <b-form-input f
                                          name="numberOfWords"
                                          type="text"
                                          min="0"
                                          v-model="currentTurnTranscriptWorkItem.numberOfWords"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="{ valid, errors }" slim
                                         v-if="!hasTopscribeDocuments  && $config.transcriptCompletion.enablePageCount">
                        <b-form-group label="Number of Pages"
                                      label-for="numberOfPages"
                                      class="col">
                            <b-form-input name="numberOfPages"
                                          type="text"
                                          min="0"
                                          v-model="currentTurnTranscriptWorkItem.numberOfPages"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enableAudioMinutes">
                        <b-form-group label="Audio Length"
                                      label-for="audioLength"
                                      class="col">
                            <audio-length-input name="audioLength"
                                                v-model="currentTurnTranscriptWorkItem.numberOfAudioSeconds"
                                                :state="vp.errors[0] ? false : null">
                            </audio-length-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>

                </b-form-row>
                <b-form-row>

                    <b-form-group label="First Word/s"
                                  label-for="firstWords"
                                  class="col">
                        <b-form-textarea name="firstWords"
                                         type="text"
                                         min="1"
                                         v-model="currentTurnTranscriptWorkItem.firstWords">
                        </b-form-textarea>
                    </b-form-group>
                </b-form-row>
                <b-form-row>
                    <b-form-group label="Last Word/s"
                                  label-for="lastWords"
                                  class="col">
                        <b-form-textarea name="lastWords"
                                         type="text"
                                         min="1"
                                         v-model="currentTurnTranscriptWorkItem.lastWords">
                        </b-form-textarea>
                    </b-form-group>

                </b-form-row>
                <b-form-row>
                    <b-form-group label="Comments"
                                  label-for="comments"
                                  class="col">
                        <b-form-textarea ref="comments"
                                         name="comments"
                                         v-model="currentTurnTranscriptWorkItem.comments">
                        </b-form-textarea>
                    </b-form-group>
                </b-form-row>


                <attachments ref="completeAttachments"
                             v-if="!hasTopscribeDocuments"
                             :all-attachment-classifications="turnTranscriptAttachmentClassificationOption ? [turnTranscriptAttachmentClassificationOption] : null"
                             attachment-classification-selection="required"
                             :related-to-id="currentTurnTranscriptWorkItem.workItemId"
                             related-to="WorkItem"
                             :current-user-account-id="userAccountId"
                             :can-modify-all-attachments="false"
                             :max-attachments="1">
                </attachments>

                <div class="card mt-2" v-if="hasTopscribeDocuments && currentTurnTranscriptWorkItem.topscribeDocumentId != null">
                    <div class="card-header d-flex justify-content-between align-items-start">
                        <h5 class="card-title">Attachments</h5>
                    </div>
                    <div class="card-body">
                        <a :href="topscribeViewUrl(currentTurnTranscriptWorkItem)" target="_blank">TOPScribe Document</a>
                    </div>
                </div>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     title="Mark As Empty"
                     :ok-title="currentTurnTranscriptWorkItem.confirmAction ? 'Confirm' : 'OK'"
                     v-model="markEmptyTurnTranscriptWorkItemDialogOpen"
                     v-on:shown="markEmptyTurnTranscriptWorkItemDialogShown"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:ok.prevent="passes(markEmptyTurnTranscriptWorkItem)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Turn Number"
                                  label-for="turnNumber"
                                  class="col-md-3">
                        <b-form-input name="turnNumber"
                                      :value="currentTurnTranscriptWorkItem.turnNumberDisplayText"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                </b-form-row>
                <b-form-row>
                    <b-form-group label="Comments"
                                  label-for="comments"
                                  class="col">
                        <b-form-textarea ref="comments"
                                         name="comments"
                                         v-model="currentTurnTranscriptWorkItem.comments">
                        </b-form-textarea>
                    </b-form-group>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="md"
                     centered
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler"
                     :title="'Split turn #'  + currentTurnTranscriptWorkItem.turnNumber "
                     v-model="splitTurnDialogOpen"
                     v-on:ok.prevent="passes(splitTurn)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>

                <b-alert v-model="showSplitTurnAlertMessage" variant="danger" dismissible>
                    <p>{{ splitTurnAlertMessage }}</p>
                </b-alert>

                <b-form-group label="Turn START:"
                              label-for="startTime"
                              label-cols-md="6"
                              content-cols-md="6"
                              read-only>
                    <b-form-input ref="startTime"
                                  name="startTime"
                                  disabled
                                  :value="formatDateTime(currentTurnTranscriptWorkItem.startTime, 'hh:mm:ssa', false)">
                    </b-form-input>
                </b-form-group>
                <b-row>
                    <b-col cols="6">
                        <b-form-radio v-model="addNewTurnBefore" class="mt-1 mb-1" value="true">Prepend new turn</b-form-radio>
                    </b-col>
                    <b-col cols="6">
                        <b-form-radio v-model="addNewTurnBefore" class="mt-1 mb-1" value="false">Append new turn</b-form-radio>
                    </b-col>
                </b-row>

                <validation-provider rules="required" name="Split time 1" v-slot="vp" slim>

                    <b-form-group label="Split time 1"
                                  label-for="splitTime1"
                                  label-cols-md="6"
                                  content-cols-md="6">

                        <b-form-input ref="splitTime1"
                                      name="splitTime1"
                                      type="time"
                                      step="1"
                                      v-model="splitTime1"
                                      :state="vp.errors[0] ? false : null">
                        </b-form-input>

                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>

                <b-form-group label="Split time 2"
                              label-for="splitTime2"
                              label-cols-md="6"
                              content-cols-md="6">


                    <b-form-input ref="splitTime2"
                                  name="splitTime2"
                                  type="time"
                                  step="1"
                                  v-model="splitTime2">
                    </b-form-input>
                </b-form-group>

                <b-form-group label="Turn END:"
                              label-for="endTime"
                              label-cols-md="6"
                              content-cols-md="6"
                              read-only>
                    <b-form-input ref="endTime"
                                  name="endTime"
                                  disabled
                                  :value="formatDateTime(currentTurnTranscriptWorkItem.endTime, 'hh:mm:ssa', false)">
                    </b-form-input>
                </b-form-group>

            </b-modal>
        </validation-observer>


        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="md"
                     centered
                     :title="turnEditTitle"
                     v-model="editTranscriptTotalsDialogOpen"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler"
                     v-on:ok.prevent="passes(updateTranscriptTotals)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row v-if="!openFirstAndLastWordEditModal">
                    <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enableWordCount">
                        <b-form-group label="Number of Words"
                                      label-for="numberOfWords"
                                      class="col">
                            <b-form-input name="numberOfWords"
                                          type="text"
                                          min="0"
                                          v-model="currentTurnTranscriptWorkItem.numberOfWords"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enablePageCount">
                        <b-form-group label="Number of Pages"
                                      label-for="numberOfPages"
                                      class="col">
                            <b-form-input name="numberOfPages"
                                          type="text"
                                          min="0"
                                          v-model="currentTurnTranscriptWorkItem.numberOfPages"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                         v-if="$config.transcriptCompletion.enableAudioMinutes">
                        <b-form-group label="Audio Length"
                                      label-for="audioLength"
                                      class="col">
                            <audio-length-input name="audioLength"
                                                v-model="currentTurnTranscriptWorkItem.numberOfAudioSeconds"
                                                :state="vp.errors[0] ? false : null">
                            </audio-length-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>

                <b-form-row>
                    <b-form-group label="First Word/s"
                                  label-for="firstWords"
                                  class="col">
                        <b-form-textarea name="firstWords"
                                         type="text"
                                         min="1"
                                         v-model="currentTurnTranscriptWorkItem.firstWords">
                        </b-form-textarea>
                    </b-form-group>
                </b-form-row>
                <b-form-row>
                    <b-form-group label="Last Word/s"
                                  label-for="lastWords"
                                  class="col">
                        <b-form-textarea name="lastWords"
                                         type="text"
                                         min="1"
                                         v-model="currentTurnTranscriptWorkItem.lastWords">
                        </b-form-textarea>
                    </b-form-group>
                </b-form-row>

            </b-modal>
        </validation-observer>


        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     ok-only
                     title="Transcript Attachment"
                     v-model="editAttachmentDialogOpen"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler"
                     v-on:ok.prevent="passes(updateAttachment)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <attachments ref="attachments"
                                 :all-attachment-classifications="editAttachmentClassifications"
                                 attachment-classification-selection="required"
                                 :related-to-id="currentTurnTranscriptWorkItem.workItemId"
                                 related-to="WorkItem"
                                 :current-user-account-id="userAccountId"
                                 :can-modify-all-attachments="false"
                                 :max-attachments="completeDialogMaxAttachments"
                                 class="w-100">
                    </attachments>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <topscribe-production-documents-dialog ref="topscribeProductionDocumentsDialog"
                                               :api-controller-url="apiUrl + '/topscribeproductiondocument/'"
                                               :file-download-url="fileDownloadUrl"
                                               :topscribe-document-id="transcriptWorkItem.topscribeDocumentId"
                                               :readonly="readonly || (transcriptWorkItem.checkedDateTimeUTC != null)"
                                               v-model="productionDocumentsDialogOpen"
                                               v-if="hasTopscribeDocuments && transcriptWorkItem">
        </topscribe-production-documents-dialog>

        <external-source-fulfillments-dialog ref="externalSourceFulfillmentsDialog"
                                               :transcript-work-item-id="transcriptWorkItem.workItemId"
                                               
                                               v-model="fulfillmentsDialogOpen"
                                               v-if="hasTopscribeDocuments && transcriptWorkItem && transcriptWorkItem.hasOrdersWithExternalSources">
        </external-source-fulfillments-dialog>

        <b-modal id="blankAudioSegmentModal"
                 v-model="openBlankAudioSegmentsModal"
                 v-on:hidden="genericModalHiddenHandler"
                 v-on:shown="genericModalShownHandler"
                 title="Manage blank audio segments" size="lg"
                 v-on:ok.prevent="saveBlankAudioSegments">
            <blank-audio-segments ref="blankAudioSegmentModal"
                                  :blank-audio-segments="blankAudioSegments"
                                  :all-blank-audio-segment-reasons="allBlankAudioSegmentReasons">
            </blank-audio-segments>
        </b-modal>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="sm"
                     centered
                     title="Create Pre-Assignment"
                     v-model="createPreAssignmentDialogOpen"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler"
                     v-on:ok.prevent="passes(createPreAssignment)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row class="mb-2">
                    Pre-assign user for Turn {{ selectedTurn ? selectedTurn.turnNumber : null }}
                </b-form-row>
                <b-form-row>
                    <validation-provider rules="required" name="Select User" v-slot="vp" slim>
                        <b-form-group label="Select User" label-for="selectPreAssignmentUser" class="col-md-12">
                            <select2 name="selectPreAssignmentUser"
                                     v-model="selectedPreAssignedUser"
                                     :options="preAssignOptions"
                                     key-property="userAccountId"
                                     text-property="userAccountDisplayName"
                                     in-b-modal
                                     allow-clear
                                     :state="vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="sm"
                     centered
                     title="Clear Pre-Assignments"
                     v-model="clearUserPreAssignmentDialogOpen"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler"
                     v-on:ok.prevent="passes(clearUserPreAssignment)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <validation-provider rules="required" name="Select User" v-slot="vp" slim>
                        <b-form-group label="Select User" label-for="selectPreAssignmentUser" class="col-md-12">
                            <select2 name="selectPreAssignmentUser"
                                     v-model="selectedPreAssignedUser"
                                     :options="preAssignedUserList"
                                     in-b-modal
                                     allow-clear
                                     :state="vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="sm"
                     centered
                     title="Create QC Pre-Assignment"
                     v-model="createQcPreAssignmentDialogOpen"
                     v-on:ok.prevent="passes(createQcPreAssignment)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row class="mb-2">
                    Pre-assign QC user for Turn {{ selectedTurn ? selectedTurn.turnNumber : null }}
                </b-form-row>
                <b-form-row>
                    <validation-provider rules="required" name="Select User" v-slot="vp" slim>
                        <b-form-group label="Select User" label-for="selectQcPreAssignmentUser" class="col-md-12">
                            <select2 name="selectQcPreAssignmentUser"
                                     v-model="selectedQcPreAssignedUser"
                                     :options="qcPreAssignOptions"
                                     key-property="userAccountId"
                                     text-property="userAccountDisplayName"
                                     in-b-modal
                                     allow-clear
                                     :state="vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="sm"
                     centered
                     title="Clear QC Pre-Assignment"
                     v-model="clearQcPreAssignmentDialogOpen"
                     v-on:ok.prevent="passes(clearQcPreAssignment)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row v-if="selectedTurn && selectedTurn.preAssignedQCUserAccountId">
                    Assigned User: {{selectedTurn.preAssignedQCUserAccountDisplayName}}
                </b-form-row>
            </b-modal>
        </validation-observer>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal id="turnZeroModal" v-model="openTurn0Modal" title="Add Turn 0" size="sm"
                     v-on:ok.prevent="passes(saveTurnZero)"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler">
                <validation-provider rules="required" name="Turn 0 start time" v-slot="vp" slim>
                    <b-form-group label="Turn 0 start time:"
                                  label-for="turnZeroStartTime"
                                  class="col-md-12">
                        <b-form-input ref="turnZeroStartTime"
                                      name="turnZeroStartTime"
                                      type="time"
                                      step="1"
                                      v-model="turnZeroStartTime"
                                      :state="vp.errors[0] ? false : null">
                        </b-form-input>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
            </b-modal>
        </validation-observer>

        <data-table-modal title="Turn Comments"
                          v-model="openViewAllCommentsModal"
                          :tableData="turnTranscriptWorkItems"
                          :columns="allCommentsColumns">
        </data-table-modal>

        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg"
                     centered
                     title="Work Item QC Check"
                     v-model="qcCheckDialogData.dialogOpen"
                     v-on:ok.prevent="passes(updateQCCheckTurnTranscriptWorkItem)"
                     v-on:hidden="genericModalHiddenHandler"
                     v-on:shown="genericModalShownHandler">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Turn Number"
                                  label-for="turnNumber"
                                  class="col-md-3">
                        <b-form-input name="turnNumber"
                                      :value="currentTurnTranscriptWorkItem.turnNumberDisplayText"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                    <b-form-group label="Completed By"
                                  label-for="completedBy"
                                  class="col-md-4">
                        <b-form-input name="completedBy"
                                      :value="currentTurnTranscriptWorkItem.completedByUserAccountDisplayName"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                    <b-form-group label="Completed At"
                                  label-for="completedAt"
                                  class="col-md-5">
                        <b-form-input name="completedAt"
                                      :value="formatDateTime(currentTurnTranscriptWorkItem.completedDateTimeUTC, 'DD/MM/yy hh:mm:ssa', false)"
                                      readonly>
                        </b-form-input>
                    </b-form-group>
                </b-form-row>


                <template v-if="qcCheckDialogData.qcSheet != null && qcCheckDialogData.qcSheet.qcErrors != null">

                    <b-form-row v-for="qcError in qcCheckDialogData.qcSheet.qcErrors" v-bind:key="qcError.workItemQCErrorType.enumValue">
                        <b-form-group :label="qcError.workItemQCErrorType.displayName"
                                      :label-for="qcError.workItemQCErrorType.stringValue"
                                      class="col">
                            <b-form-input :ref="qcError.workItemQCErrorType.stringValue"
                                          :name="qcError.workItemQCErrorType.stringValue"
                                          v-model="qcError.count">
                            </b-form-input>
                        </b-form-group>
                    </b-form-row>

                    <b-form-row>
                        <b-form-group label="QC Completed"
                                      label-for="qcDateTime"
                                      class="col">
                            <ejs-datetimepicker ref="qcDateTime"
                                                name="qcDateTime"
                                                v-model="qcCheckDialogData.qcSheet.qcDateTime"
                                                format="d/M/yyyy h:mma"></ejs-datetimepicker>
                        </b-form-group>

                    </b-form-row>

                    <b-form-row>
                        <validation-provider rules="required|integer|min_value:0" name="Number of Words" v-slot="{ valid, errors }" slim
                                             v-if="!hasTopscribeDocuments && $config.transcriptCompletion.enableWordCount">
                            <b-form-group label="Number of Words"
                                          label-for="numberOfWords"
                                          class="col">
                                <b-form-input f
                                              name="numberOfWords"
                                              type="text"
                                              min="0"
                                              v-model="qcCheckDialogData.qcSheet.numberOfWords"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider rules="required|integer|min_value:0" name="Number of Pages" v-slot="{ valid, errors }" slim
                                             v-if="!hasTopscribeDocuments  && $config.transcriptCompletion.enablePageCount">
                            <b-form-group label="Number of Pages"
                                          label-for="numberOfPages"
                                          class="col">
                                <b-form-input name="numberOfPages"
                                              type="text"
                                              min="0"
                                              v-model="qcCheckDialogData.qcSheet.numberOfPages"
                                              :state="errors[0] ? false : null">
                                </b-form-input>
                                <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>
                        <validation-provider rules="required|min_value:0" name="Audio Length" v-slot="vp" slim
                                             v-if="$config.transcriptCompletion.enableAudioMinutes">
                            <b-form-group label="Audio Length"
                                          label-for="audioLength"
                                          class="col">
                                <audio-length-input name="audioLength"
                                                    v-model="qcCheckDialogData.qcSheet.numberOfAudioSeconds"
                                                    :state="vp.errors[0] ? false : null">
                                </audio-length-input>
                                <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </validation-provider>

                    </b-form-row>
                </template>

                <attachments v-if="!currentTurnTranscriptWorkItem.qcDateTimeUTC"
                             ref="completeQCAttachments"
                             :all-attachment-classifications="correctedTurnTranscriptAttachmentClassificationOption ? [correctedTurnTranscriptAttachmentClassificationOption] : null"
                             attachment-classification-selection="required"
                             :related-to-id="currentTurnTranscriptWorkItem.workItemId"
                             related-to="WorkItem"
                             :current-user-account-id="userAccountId"
                             :can-modify-all-attachments="false"
                             :max-attachments="completeDialogMaxAttachments">
                </attachments>

            </b-modal>
        </validation-observer>

        <data-table-modal title="First and Last Words" v-model="openFirstAndLastWordModal" :tableData="turnTranscriptWorkItems" :columns="firstAndLastWordColumns"></data-table-modal>

    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
    import axios from 'axios';
    import moment from 'moment';
    import fileDownload from 'js-file-download';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required, integer, min_value, double } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('integer', integer);
    extend('min_value', min_value);
    extend('double', double);
    localize('en', en);

    import AudioLengthInput from './AudioLengthInput';

    import TranscriptMixins from '../mixins/TranscriptMixins';
    import BlankAudioSegments from './BlankAudioSegments';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import ErrorsMixin from '../mixins/ErrorsMixin';
    import UuidMixins from '../mixins/UuidMixins';
    import TopscribeProductionDocumentsDialog from './TOPScribe/TopscribeProductionDocumentsDialog';
    import ExternalSourceFulfillmentsDialog from './Worksheet/ExternalSourceFulfillmentsDialog';

    Vue.use(BlankAudioSegments);
    Vue.use(BootstrapVue);
    Vue.use(BootstrapVueIcons);
    Vue.component('audio-length-input', AudioLengthInput);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);

    import DataTableModal from '../components/common/DataTableModal';
    Vue.component('data-table-modal', DataTableModal);


    export default {
        name: 'TurnTranscriptWorkitems',
        mixins: [DateTimeMixin, ErrorsMixin, TranscriptMixins, UuidMixins],
        components: {
            'topscribe-production-documents-dialog': TopscribeProductionDocumentsDialog,
            'external-source-fulfillments-dialog': ExternalSourceFulfillmentsDialog
        },
        props: {
            apiUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                default: null
            },
            topscribeEditorBaseUrl: {
                type: String,
                default: null
            },
            topscribeViewBaseUrl: {
                type: String,
                default: null
            },
            transcriptWorkItemId: {
                type: String,
                required: true
            },
            userAccountId: {
                type: String,
                required: true
            },
            turnLength: {
                type: Number,
                default: 10
            },
            permissions: {
                type: Object,
                required: true
            },
            hasTopscribeDocuments: {
                type: Boolean,
                default: false
            },
            turnsReadonly: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            },
            allBlankAudioSegmentReasons: {
                type: Array,
                required: true
            },
            assignedStaff: {
                type: Array,
                default: null
            },
            turnTranscriptAttachmentClassificationOption: {
                type: Object,
                default: null
            },
            correctedTurnTranscriptAttachmentClassificationOption: {
                type: Object,
                default: null
            }
        },
        data: function () {
            return {
                //isAnyModalOpen: false,
                qcMode: false,
                transcriptWorkItem: null,
                turnTranscriptWorkItems: [],
                blankAudioSegments: [],

                hasTurnZero: false,
                openTurn0Modal: false,
                openViewAllCommentsModal: false,
                allCommentsColumns: [
                    {
                        key: "turnNumberDisplayText", label: "Turn Number"
                    },
                    {
                        key: "completedByUserAccountDisplayName", label: "Completed By"
                    },
                    {
                        key: "comments", label: "Comments"
                    }
                ],
                turnZeroStartTime: null,

                openBlankAudioSegmentsModal: false,
                mergedTurnBlankAudioSegments: [],
                showSplitTurnAlertMessage: false,
                splitTurnAlertMessage: '',

                allAttachmentsColumns: [
                    {
                        key: "turnNumberDisplayText", label: "Turn Number"
                    },
                    {
                        key: "completedByUserAccountDisplayName", label: "Completed By"
                    },
                    {
                        key: "completedDateTimeUTC",
                        label: "Completed",
                        formatter: value => {
                            return this.formatDateTime(value, 'DD/MM/yy hh:mma', false);
                        },
                    }
                ],
                openSelectTurnAttachmentsModal: false,
                previousSelectedKeys: [],
                previousQCSelectedKeys: [],
                currentZipFile: null,


                splitTurnDialogOpen: false,
                splitTime1: null,
                splitTime2: null,
                addNewTurnBefore: false,

                nextAvailableTurnNumber: 0,
                nextAvailableSubTurnNumber: null,
                nextAvailableTurnNumberForQc: 0,
                nextAvailableSubTurnNumberForQc: null,
                myActiveTurnNumber: null,
                lastTurnNumber: 0,
                lastSubTurnNumber: null,
                currentTurnTranscriptWorkItem: {
                    workItemId: null,
                    turnNumber: 0,
                    startTime: null,
                    endTime: null,
                    comments: null,
                    numberOfWords: null,
                    numberOfAudioSeconds: null,
                    numberOfPages: null,
                    firstWords: null,
                    lastWords: null
                },
                currentErrors: [],
                additionalTurnsLength: this.turnLength,
                additionalTurns: 1,
                addTurnTranscriptWorkItemsDialogOpen: false,
                currentComments: null,
                //commentsDialogOpen: false,
                completeTurnTranscriptWorkItemDialogOpen: false,
                markEmptyTurnTranscriptWorkItemDialogOpen: false,
                editTranscriptTotalsDialogOpen: false,
                editAttachmentDialogOpen: false,
                editAttachmentClassifications: [],
                tableFields: [
                    {
                        key: 'turnNumberDisplayText',
                        label: 'Turn',
                        class: 'fit'
                    },
                    {
                        key: 'startTime',
                        label: 'Start Time',
                        formatter: value => {
                            return this.formatDateTime(value, 'hh:mm:ssa', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'endTime',
                        label: 'End Time',
                        formatter: value => {
                            return this.formatDateTime(value, 'hh:mm:ssa', false);
                        },
                        class: 'fit'
                    },
                    {
                        key: 'statusDisplayName',
                        label: 'Status/Reason'
                    },
                    {
                        key: 'info',
                        label: 'Info'
                    },
                    {
                        key: 'actions',
                        label: 'Actions',
                        class: 'fit text-right'
                    }
                ],
                productionDocumentsDialogOpen: false,
                fulfillmentsDialogOpen: false,

                createPreAssignmentDialogOpen: false,
                clearUserPreAssignmentDialogOpen: false,
                preAssignedUserList: {
                    value: null,
                    text: null
                },
                selectedPreAssignedUser: null,
                selectedTurn: null,
                qcCheckDialogData: {
                    dialogOpen: false,
                    qcSheet: null,
                    errors: [],
                },

                createQcPreAssignmentDialogOpen: false,
                clearQcPreAssignmentDialogOpen: false,
                preAssignedQcUserList: {
                    value: null,
                    text: null
                },
                selectedQcPreAssignedUser: null,
                openFirstAndLastWordModal: false,
                firstAndLastWordColumns: [
                    {
                        key: "turnNumberDisplayText",
                        label: "Turn Number",
                        canCopy: false
                    },
                    {
                        key: 'completedByUserAccountDisplayName',
                        label: 'Completed By',
                        canCopy: false
                    },
                    {
                        key: 'firstWords',
                        label: 'First Word/s',
                        canCopy: true
                    },
                    {
                        key: 'lastWords',
                        label: 'Last Word/s',
                        canCopy: true
                    }
                ],
                openFirstAndLastWordEditModal: false,
                turnEditTitle: 'Update Turn Totals',
            };
        },

        computed: {
            showErrors: function () {
                return this.currentErrors.length > 0;
            },
            choosePreviousSelectedKeys: function () {
                if (this.qcMode) {
                    return this.previousQCSelectedKeys
                }
                else 
                    return this.previousSelectedKeys
            },
            myTotals: function () {
                var myTotalNumberOfWords = 0;
                var myTotalNumberOfAudioSeconds = 0;
                var myTotalNumberOfFolios = 0;
                var myTotalNumberOfPages = 0;
                this.turnTranscriptWorkItems.forEach(turn => {
                    if (turn.completedByUserAccountId == this.userAccountId) {
                        myTotalNumberOfWords = myTotalNumberOfWords + (turn.numberOfWords ? turn.numberOfWords : 0);
                        var numberOfFolios = this.getNumberOfFolios(turn.numberOfWords);
                        myTotalNumberOfFolios = myTotalNumberOfFolios + (numberOfFolios ? numberOfFolios : 0);
                        myTotalNumberOfPages = myTotalNumberOfPages + (turn.numberOfPages ? turn.numberOfPages : 0);
                        myTotalNumberOfAudioSeconds = myTotalNumberOfAudioSeconds + (turn.numberOfAudioSeconds ? turn.numberOfAudioSeconds : 0);
                    }
                });
                var myTotals = '';
                if (myTotalNumberOfWords > 0) {
                    myTotals = 'My Totals: ' + myTotalNumberOfWords + ' words';
                    if (myTotalNumberOfFolios > 0) {
                        myTotals += ' / ' + myTotalNumberOfFolios + ' folios';
                    }
                    if (myTotalNumberOfPages > 0) {
                        myTotals += ' on ' + myTotalNumberOfPages + ' pages';
                    }
                    if (myTotalNumberOfAudioSeconds > 0) {
                        var audioDisplay = this.formatSecondsAsDuration(myTotalNumberOfAudioSeconds);
                        myTotals += ' for ' + audioDisplay;
                    }
                }
                return myTotals;
            },
            allTotals: function () {
                var allTotalNumberOfWords = 0;
                var allTotalNumberOfAudioSeconds = 0;
                var allTotalNumberOfFolios = 0;
                var allTotalNumberOfPages = 0;
                this.turnTranscriptWorkItems.forEach(turn => {
                        allTotalNumberOfWords = allTotalNumberOfWords + (turn.numberOfWords ? turn.numberOfWords : 0);
                        var numberOfFolios = this.getNumberOfFolios(turn.numberOfWords);
                        allTotalNumberOfFolios = allTotalNumberOfFolios + (numberOfFolios ? numberOfFolios : 0);
                        allTotalNumberOfPages = allTotalNumberOfPages + (turn.numberOfPages ? turn.numberOfPages : 0);
                        allTotalNumberOfAudioSeconds = allTotalNumberOfAudioSeconds + (turn.numberOfAudioSeconds ? turn.numberOfAudioSeconds : 0);
                });
                var allTotals = '';
                if (allTotalNumberOfWords > 0) {
                    allTotals = 'All Totals: ' + allTotalNumberOfWords + ' words';
                    if (allTotalNumberOfFolios > 0) {
                        allTotals += ' / ' + allTotalNumberOfFolios + ' folios';
                    }
                    if (allTotalNumberOfPages > 0) {
                        allTotals += ' on ' + allTotalNumberOfPages + ' pages';
                    }
                    if (allTotalNumberOfAudioSeconds > 0) {
                        var audioDisplay = this.formatSecondsAsDuration(allTotalNumberOfAudioSeconds);
                        allTotals += ' for ' + audioDisplay;
                    }
                }
                return allTotals;
            },
            downloadTurnsForQCActionAvailable: function () {
                if (this.permissions && this.permissions.canCheckTranscriptTurns && !this.hasTopscribeDocuments) return true;
                return false;
            },
            addTurnZeroActionAvailable: function () {
                if (this.readonly || this.turnsReadonly || !this.permissions.canManageTurns) return false;
                if (!this.transcriptWorkItem) return false;
                if (!this.transcriptWorkItem.workStarted) return false;
                return !this.hasTurnZero;
            },
            preAssignOptions: function () {
                return this.assignedStaff.filter(s => {
                    if (!s.workItemRelatedRoles) return false;
                    if ((s.workItemRelatedRoles.enumValue & this.$enum.WorkItemRelatedRole.Stenographer) == this.$enum.WorkItemRelatedRole.Stenographer) return true;
                    if ((s.workItemRelatedRoles.enumValue & this.$enum.WorkItemRelatedRole.Transcriber) == this.$enum.WorkItemRelatedRole.Transcriber) return true;
                    if ((s.workItemRelatedRoles.enumValue & this.$enum.WorkItemRelatedRole.Editor) == this.$enum.WorkItemRelatedRole.Editor) return true;
                    if ((s.workItemRelatedRoles.enumValue & this.$enum.WorkItemRelatedRole.Coordinator) == this.$enum.WorkItemRelatedRole.Coordinator) return true;
                    return false;
                });
            },
            qcPreAssignOptions: function () {
                return this.assignedStaff.filter(s => {
                    if (!s.workItemRelatedRoles) return false;
                    if ((s.workItemRelatedRoles.enumValue & this.$enum.WorkItemRelatedRole.QC) == this.$enum.WorkItemRelatedRole.QC) return true; //the workItemRelatedRoles.enumValue has to contain the QC flag
                    return false;
                });
            },
            clearUserPreAssignmentAvailable: function () {
                if (this.readonly || this.turnsReadonly) return false;
                if (!this.transcriptWorkItem) return false;
                if (this.transcriptWorkItem.mergedDateTimeUTC) return false;
                if ((this.preAssignedUserList == null) || (this.preAssignedUserList.length == 0)) return false;
                return this.permissions.canManageTurns;
            },
            turnAttachments: function () {
                if (this.qcMode) {
                    return this.turnTranscriptWorkItems
                        .filter(turnTranscriptWorkItem => {
                            return (turnTranscriptWorkItem.completedDateTimeUTC != null) && (turnTranscriptWorkItem.preAssignedQCUserAccountId == this.userAccountId) &&
                                (!this.isUuidNullOrEmpty(turnTranscriptWorkItem.turnTranscriptFileId));
                        });
                }
                else
                    return this.turnTranscriptWorkItems
                        .filter(turnTranscriptWorkItem => {
                            return (turnTranscriptWorkItem.completedDateTimeUTC != null) &&
                                (!this.isUuidNullOrEmpty(turnTranscriptWorkItem.turnTranscriptFileId) || !this.isUuidNullOrEmpty(turnTranscriptWorkItem.correctedTurnTranscriptFileId));
                        });
            },
            completeDialogMaxAttachments: function () {
                var maxAttachments = {};
                if (this.turnTranscriptAttachmentClassificationOption) {
                    maxAttachments[this.turnTranscriptAttachmentClassificationOption.value] = 1;
                }
                if (this.correctedTurnTranscriptAttachmentClassificationOption) {
                    maxAttachments[this.correctedTurnTranscriptAttachmentClassificationOption.value] = 1;
                }
                return maxAttachments;
        },
            mergeAllAvailableTurnsAvailable: function () {
                var totalMergable = 0;
                this.turnTranscriptWorkItems.forEach(turn => {
                    if (this.mergeTurnActionAvailable(turn)) { totalMergable++; }
                });                

                return totalMergable > 0;
            }
        },
        methods: {
            // if modal does not have any other method which handles the hiding process it calls this one
            genericModalHiddenHandler: function () {
                this.$emit('modalOpen', false);
            },
            // if modal does not have any other method which handles the showing process it calls this one
            genericModalShownHandler: function () {
                this.$emit('modalOpen', true);
            },
            copyToClipboard: function (args) {
                navigator.clipboard.writeText(args);
                this.$bvToast.toast(args, {
                    title: 'Copied to Clipboard',
                    variant: 'success',
                    appendToast: true
                });
            },
            openFirstLastWordEdit: function (turnTranscriptWorkItem) {
                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.turnEditTitle = 'Edit First and Last Words';
                this.openFirstAndLastWordEditModal = true;
                this.editTranscriptTotalsDialogOpen = true;
            },

            reload: function () {

                if (!this.openBlankAudioSegmentsModal) {

                    var promises = [];
                    promises.push(this.reloadTranscript());
                    if (this.productionDocumentsDialogOpen) promises.push(this.$refs.topscribeProductionDocumentsDialog.reload());
                    if (this.fulfillmentsDialogOpen) promises.push(this.$refs.externalSourceFulfillmentsDialog.reload());

                    var turnBlankAudioSegmentsPromises = [];
                    turnBlankAudioSegmentsPromises.push(this.reloadTurns());
                    turnBlankAudioSegmentsPromises.push(this.reloadBlankAudioSegments());
                    promises.push(
                        Promise.all(turnBlankAudioSegmentsPromises)
                            .then(() => {
                                this.mergeBlankAudioSegments();
                            }));
                    return Promise.all(promises);
                }
            },
            closeFirstLastWordModal: function () {
                this.openFirstAndLastWordModal = false
            },
            openTurnZeroModal: function () {
                this.openTurn0Modal = true;
            },
            openBlankAudioModal: function () {
                this.openBlankAudioSegmentsModal = true;
            },
            closeAllCommentsModal: function () {
                this.openViewAllCommentsModal = false
            },
            downloadTurnTranscript: function (fileId) {
                var url = this.$urlHelper.getFileDownloadUrl(fileId);
                window.open(url);
            },
            closeAllAttachmentsModal: function () {
                this.openSelectTurnAttachmentsModal = false
            },

            getDownloadedTurnTranscriptFileIds: function() {
                var downloadedTurnTranscriptFileIds = localStorage.getItem('downloaded_turns_' + this.transcriptWorkItemId);
                if (downloadedTurnTranscriptFileIds == null) return [];
                return downloadedTurnTranscriptFileIds.split(',');
            },

            setDownloadedTurnTranscriptFileIds: function (downloadedTurnTranscriptFileIds) {
                if (downloadedTurnTranscriptFileIds == null) {
                    localStorage.removeItem('downloaded_turns_' + this.transcriptWorkItemId);
                } else {
                    localStorage.setItem('downloaded_turns_' + this.transcriptWorkItemId, downloadedTurnTranscriptFileIds);
                }
            },

            getDownloadedQCTurnTranscriptFileIds: function () {
                var downloadedTurnTranscriptFileIds = localStorage.getItem('qc_downloaded_turns_' + this.transcriptWorkItemId);
                if (downloadedTurnTranscriptFileIds == null) return [];
                return downloadedTurnTranscriptFileIds.split(',');
            },

            setDownloadedQCTurnTranscriptFileIds: function (downloadedTurnTranscriptFileIds) {
                if (downloadedTurnTranscriptFileIds == null) {
                    localStorage.removeItem('qc_downloaded_turns_' + this.transcriptWorkItemId);
                } else {
                    localStorage.setItem('qc_downloaded_turns_' + this.transcriptWorkItemId, downloadedTurnTranscriptFileIds);
                }
            },

            markTurnTranscriptFileAsDownloaded: function (turnTranscriptFileId) { 
                var downloadedTurnTranscriptFileIds = this.getDownloadedTurnTranscriptFileIds();
                if (!downloadedTurnTranscriptFileIds.includes(turnTranscriptFileId)) 
                    downloadedTurnTranscriptFileIds.push(turnTranscriptFileId);

                if (this.qcMode) 
                    this.setDownloadedQCTurnTranscriptFileIds(downloadedTurnTranscriptFileIds);
                else
                    this.setDownloadedTurnTranscriptFileIds(downloadedTurnTranscriptFileIds);
            },
            turnTranscriptFileWasDownloadedPreviously: function (turnTranscriptFileId) {
                var downloadedTurnTranscriptFileIds = this.getDownloadedTurnTranscriptFileIds();
                return downloadedTurnTranscriptFileIds.includes(turnTranscriptFileId);
            },

            okAllAttachmentsModal: function (selectedKeys) {
                if (selectedKeys) {
                    var selectedTurnTranscriptFileIds = 
                        this.turnTranscriptWorkItems
                            .filter(t => {
                                return selectedKeys.includes(t.workItemId) &&
                                    ((!this.qcMode && !this.isUuidNullOrEmpty(t.turnTranscriptFileId) || !this.isUuidNullOrEmpty(t.correctedTurnTranscriptFileId) ||
                                    (this.qcMode && !this.isUuidNullOrEmpty(t.turnTranscriptFileId))));
                            })
                            .map(t => !this.isUuidNullOrEmpty(t.correctedTurnTranscriptFileId) ? t.correctedTurnTranscriptFileId : t.turnTranscriptFileId);

                    var downloadedTurnTranscriptFileIds;

                    if(this.qcMode)
                        downloadedTurnTranscriptFileIds = this.getDownloadedQCTurnTranscriptFileIds();
                    else
                        downloadedTurnTranscriptFileIds = this.getDownloadedTurnTranscriptFileIds();


                    selectedTurnTranscriptFileIds.forEach(turnTranscriptFileId => {
                        if (!downloadedTurnTranscriptFileIds.includes(turnTranscriptFileId)) {
                            downloadedTurnTranscriptFileIds.push(turnTranscriptFileId);
                        }
                    });
                    if (this.qcMode) {
                        this.setDownloadedQCTurnTranscriptFileIds(downloadedTurnTranscriptFileIds);
                    }
                    else
                        this.setDownloadedTurnTranscriptFileIds(downloadedTurnTranscriptFileIds);
                }
                
                // data var used for the axios call to download selected docs
                var data = {
                    qcMode: this.qcMode,
                    turnWorkItemIds: selectedKeys
                }

                axios
                    .put(this.apiUrl + '/workitem/DownloadTurnTranscripts', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            responseType: 'blob'
                        })
                    .then(response => {
                        var contentType = response.headers["content-type"];
                        var filename = "Turns.zip";
                        var contentDisposition = response.headers["content-disposition"];
                        if (contentDisposition) {
                            var match = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
                            if (match) {
                                filename = match[1].trim().replace(/^('|")|('|")$/gm, '');
                            }
                        }
                        fileDownload(response.data, filename, contentType);
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },
            saveTurnZero: function () {

                var referenceDate = this.now();
                var startTime = this.setDateComponent(this.parseDateTime(this.turnZeroStartTime, 'HH:mm:ss', false, false), referenceDate);
                var data = {
                    startTime: this.formatDateTime(startTime, 'YYYY-MM-DDTHH:mm:ss', false, false)
                }


                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.transcriptWorkItemId + '/addturnzero', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.openTurn0Modal = false;
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            saveBlankAudioSegments: function () {
                var data = [];
                if (this.blankAudioSegments) {
                    this.blankAudioSegments.forEach(blankAudioSegment => {
                        data.push({
                            //this is to make sure that we pass datetime strings without timezone conversion to the server - if we let JSON.stringify to the conversion it will convert everything to UTC
                            startTime: this.formatDateTime(blankAudioSegment.startTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                            endTime: this.formatDateTime(blankAudioSegment.endTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                            reason: blankAudioSegment.reason
                        })
                    })
                }
                var url = this.$urlHelper.getApiUrl("blankaudiosegment", null, [this.transcriptWorkItemId]);

                axios
                    .put(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.$bvToast.toast('Blank audio segments saved', {
                            title: 'SUCCESS',
                            variant: 'success',
                            appendToast: true
                        });
                        this.openBlankAudioSegmentsModal = false;
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            },

            rowClass(item, type) {
                if (!item || type !== 'row') return;
                var colorClass = '';

                if (item.blankAudioSegmentId)
                    colorClass = 'blank-audio-segment-bg';
                else
                    colorClass = 'turn-bg';

                return colorClass;
            },

            mergeBlankAudioSegments: function () {

                this.mergedTurnBlankAudioSegments = this.turnTranscriptWorkItems.concat(this.blankAudioSegments);

                this.mergedTurnBlankAudioSegments.sort((a, b) => {
                    const turnA = a.startTime;
                    const turnB = b.startTime;

                    let comparison = 0;
                    if (turnA > turnB) {
                        comparison = 1;
                    } else if (turnA < turnB) {
                        comparison = -1;
                    }
                    return comparison;
                });



            },
            reloadTranscript: function () {
                var currentErrors = [];
                return axios
                    .get(this.apiUrl + '/workitem/' + this.transcriptWorkItemId)
                    .then(response => {
                        this.transcriptWorkItem = response.data;
                    })
                    .catch(ex => {
                        currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            reloadTurns: function () {
                var currentErrors = [];
                var incompleteTurns = 0;
                return axios
                    .get(this.apiUrl + '/workitem/turntranscript/' + this.transcriptWorkItemId)
                    .then(response => {
                        this.nextAvailableTurnNumber = 0;
                        this.nextAvailableSubTurnNumber = null;
                        this.nextAvailableTurnNumberForQc = 0;
                        this.nextAvailableSubTurnNumberForQc = null;
                        this.myActiveTurnNumber = null;
                        this.lastTurnNumber = 0;
                        this.lastSubTurnNumber = null;
                        var foundAvailablePreAssignedTurn = false;
                        var foundAvailableTurn = false;
                        var foundAvailablePreAssignedTurnForQc = false;
                        var foundAvailableTurnForQc = false;
                        this.hasTurnZero = false;
                        this.turnTranscriptWorkItems = $.map(response.data, obj => {
                            if (obj.inProgressByUserAccountId == this.userAccountId ||
                                (obj.qcInProgressByUserAccountId == this.userAccountId && !obj.qcDateTimeUTC)) this.myActiveTurnNumber = obj.turnNumberDisplayText;

                            if (((!foundAvailableTurnForQc && obj.preAssignedQCUserAccountId == null) ||
                                (!foundAvailablePreAssignedTurnForQc && obj.preAssignedQCUserAccountId == this.userAccountId)) &&
                                obj.completedDateTimeUTC && !obj.qcDateTimeUTC && !obj.qcInProgressSinceDateTimeUTC && !obj.isEmptyTurn
                                && obj.completedByUserAccountId != this.userAccountId) {
                                this.nextAvailableTurnNumberForQc = obj.turnNumber;
                                this.nextAvailableSubTurnNumberForQc = obj.subTurnNumber;
                                foundAvailableTurnForQc = true;
                                if (obj.preAssignedQCUserAccountId == this.userAccountId) foundAvailablePreAssignedTurnForQc = true;
                            }
                            if ((((!foundAvailableTurn && (obj.preAssignedUserAccountId == null)) ||
                                (!foundAvailablePreAssignedTurn) && (obj.preAssignedUserAccountId == this.userAccountId))) &&
                                !obj.completedDateTimeUTC &&
                                !obj.inProgressSinceDateTimeUTC) {
                                this.nextAvailableTurnNumber = obj.turnNumber;
                                this.nextAvailableSubTurnNumber = obj.subTurnNumber;
                                foundAvailableTurn = true;
                                if (obj.preAssignedUserAccountId == this.userAccountId) foundAvailablePreAssignedTurn = true;
                            }
                            if (!obj.completedDateTimeUTC) {
                                incompleteTurns = incompleteTurns + 1;
                            }
                            if (obj.turnNumber > this.lastTurnNumber) {
                                this.lastTurnNumber = obj.turnNumber;
                                this.lastSubTurnNumber = obj.subTurnNumber;
                            } else if ((obj.turnNumber == this.lastTurnNumber) && (obj.subTurnNumber != null) && ((this.lastSubTurnNumber == null) || (obj.subTurnNumber > this.lastSubTurnNumber))) {
                                this.lastSubTurnNumber = obj.subTurnNumber;
                            }
                            if (obj.turnNumber == 0) this.hasTurnZero = true;
                            obj.startTime = this.parseDateTime(obj.startTime, null, false);
                            obj.endTime = this.parseDateTime(obj.endTime, null, false);

                            if (obj.qcDateTime != null) obj.qcDateTime = this.parseDateTime(obj.qcDateTime, null, false);

                            return obj;
                        });

                        var tempUserList = [];
                        this.turnTranscriptWorkItems.forEach(turnTranscriptWorkItem => {
                            if (turnTranscriptWorkItem.preAssignedUserAccountId &&
                                !turnTranscriptWorkItem.completedDateTimeUTC &&
                                !turnTranscriptWorkItem.inProgressSinceDateTimeUTC) {
                                if (tempUserList.findIndex(value => { return value.value == turnTranscriptWorkItem.preAssignedUserAccountId }) < 0) {
                                    tempUserList.push({
                                        value: turnTranscriptWorkItem.preAssignedUserAccountId,
                                        text: turnTranscriptWorkItem.preAssignedUserAccountDisplayName
                                    });
                                }
                            }
                        });
                        tempUserList.sort((a, b) => {
                            if (a.text < b.text) return -1;
                            if (a.text > b.text) return 1;
                            return 0;
                        })
                        this.preAssignedUserList = tempUserList;
                    })
                    .catch(ex => {
                        currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        } else {
                            this.$emit('change', incompleteTurns)
                        }
                    });
            },
            reloadBlankAudioSegments: function () {
                var currentErrors = [];
                return axios
                    .get(this.apiUrl + '/blankaudiosegment/' + this.transcriptWorkItemId)
                    .then(response => {
                        this.blankAudioSegments = $.map(response.data, obj => {
                            obj.startTime = this.parseDateTime(obj.startTime, null, false);
                            obj.endTime = this.parseDateTime(obj.endTime, null, false);
                            return obj;
                        });
                    })
                    .catch(ex => {
                        currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            openAddTurnTranscriptWorkItemsDialog: function () {
                this.additionalTurnsLength = this.turnLength;
                this.addTurnTranscriptWorkItemsDialogOpen = true;
            },
            addTurnTranscriptWorkItems: function () {
                this.currentErrors = [];
                var data = {
                    turnLength: this.additionalTurnsLength,
                    turns: this.additionalTurns,
                    blankAudioSegments: []
                };

                if (this.blankAudioSegments) {
                    this.blankAudioSegments.forEach(blankAudioSegment => {
                        data.blankAudioSegments.push({
                            //this is to make sure that we pass datetime strings without timezone conversion to the server - if we let JSON.stringify to the conversion it will convert everything to UTC
                            startTime: this.formatDateTime(blankAudioSegment.startTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                            endTime: this.formatDateTime(blankAudioSegment.endTime, 'YYYY-MM-DDTHH:mm:ss', false, false),
                            reason: blankAudioSegment.reason
                        })
                    })
                }

                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.transcriptWorkItemId + '/add', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.addTurnTranscriptWorkItemsDialogOpen = false;
                        }
                    });
            },
            trimTurnTranscriptWorkItems: function () {
                this.currentErrors = [];
                axios
                    .get(this.apiUrl + '/workitem/turntranscript/' + this.transcriptWorkItemId + '/trim')
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            canViewTurnDetails: function (turnTranscriptWorkItem) {
                if (this.permissions.canViewAllDetails) return true;
                return (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId);
            },
            canViewTurnAttachments: function (turnTranscriptWorkItem, correctedTurnTranscript = false) {
                if (turnTranscriptWorkItem.isEmptyTurn) return false;
                if (this.hasTopscribeDocuments) return false;
                if (!turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (correctedTurnTranscript) {
                    if (!turnTranscriptWorkItem.qcDateTimeUTC) return false;
                }
                if (this.permissions.canViewAllDetails) return true;
                if (turnTranscriptWorkItem.qcInProgressByUserAccountId == this.userAccountId) return true;
                return (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId);
            },
            canModifyTurnAttachment: function (turnTranscriptWorkItem, correctedTurnTranscript = false) {
                if (turnTranscriptWorkItem.isEmptyTurn) return false;
                if (this.hasTopscribeDocuments) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                if (turnTranscriptWorkItem.mergedDateTimeUTC != null) return false;
                if (this.transcriptWorkItem != null && this.transcriptWorkItem.mergedDateTimeUTC != null) return false;
                if (!correctedTurnTranscript) {
                    if (turnTranscriptWorkItem.qcInProgressSinceDateTimeUTC != null) return false;
                    if (turnTranscriptWorkItem.qcDateTimeUTC != null) return false;
                    if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return true;
                } else {
                    if (turnTranscriptWorkItem.qcByUserAccountId == this.userAccountId) return true;
                }
                return false;
            },
            startWorkActionAvailable: function (workItem) {
                if (this.readonly) return false;
                if (workItem.workItemId == this.transcriptWorkItemId) {
                    if (!this.permissions.canMergeTranscript && !this.permissions.canCheckTranscript) return false;
                    return (!workItem.checkedDateTimeUTC && !workItem.inProgressSinceDateTimeUTC);
                } else {
                    if (this.turnsReadonly) return false;
                    if (!this.permissions.canStartWork) return false;
                    if (this.myActiveTurnNumber != null) return false;
                    return (!workItem.completedDateTimeUTC &&
                        !workItem.inProgressSinceDateTimeUTC &&
                        (workItem.turnNumber == this.nextAvailableTurnNumber) &&
                        (workItem.subTurnNumber == this.nextAvailableSubTurnNumber));
                }
            },
            startWork: function (workItem) {
                var data = {
                    action: 'startWork'
                };
                axios
                    .put(this.apiUrl + '/workitem/' + workItem.workItemId + '/status', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then((response) => {
                        this.reload();
                        if (response && response.data) {
                            var updatedWorkItem = response.data;
                            if (this.openEditorActionAvailable(updatedWorkItem)) {
                                this.openEditor(updatedWorkItem);
                            }
                        }
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            canViewQcDetails: function (turnTranscriptWorkItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.permissions.canViewAllDetails) return true;
                if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return true;
                return (turnTranscriptWorkItem.qcInProgressByUserAccountId == this.userAccountId);
            },
            startQcActionAvailable: function (workItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.readonly || this.turnsReadonly) return false;
                if (this.permissions == null || !this.permissions.canCheckTranscriptTurns) return false;
                if (workItem.isEmptyTurn) return false;
                if (workItem.completedByUserAccountId == this.userAccountId) return false;
                if (workItem.completedDateTimeUTC == null) return false;
                if (workItem.qcInProgressSinceDateTimeUTC) return false;
                if (workItem.qcDateTimeUTC) return false;
                if (this.myActiveTurnNumber != null) return false;
                return (workItem.turnNumber == this.nextAvailableTurnNumberForQc && workItem.subTurnNumber == this.nextAvailableSubTurnNumberForQc);
            },
            startQc: function (workItem) {
                this.toggleQc(workItem, 'startQC');
            },
            endQcActionAvailable: function (workItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.readonly || this.turnsReadonly) return false;
                if (this.permissions == null || !this.permissions.canCheckTranscriptTurns) return false;
                if (workItem.qcInProgressByUserAccountId != this.userAccountId) return false;
                if (!workItem.qcInProgressSinceDateTimeUTC) return false;
                if (workItem.qcDateTimeUTC) return false;
                return true;
            },
            endQc: function (workItem) {
                this.toggleQc(workItem, 'endQC');
            },
            toggleQc: function (workItem, action) {
                var data = {
                    action: action
                };
                axios
                    .put(this.apiUrl + '/workitem/' + workItem.workItemId + '/status', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            openEditorActionAvailable: function (workItem) {
                if (this.readonly) return false;
                if (workItem.completedDateTimeUTC) return false;
                if (!workItem.inProgressByUserAccountId) return false;
                if (workItem.inProgressByUserAccountId != this.userAccountId) return false;
                return (this.topscribeEditorBaseUrl && workItem.topscribeDocumentId);
            },
            openEditor: function (workItem) {
                var topscribeEditorUrl = '';
                if (this.topscribeEditorBaseUrl && workItem.topscribeDocumentId) {
                    topscribeEditorUrl = this.topscribeEditorBaseUrl;
                    if (!topscribeEditorUrl.endsWith('/')) topscribeEditorUrl += '/';
                    topscribeEditorUrl += workItem.topscribeDocumentId;
                }
                if (topscribeEditorUrl) {
                    window.open(topscribeEditorUrl);
                }
            },
            viewDocumentActionAvailable: function (workItem) {
                if (workItem.inProgressByUserAccountId != null) return false;
                if (this.topscribeViewBaseUrl && workItem.topscribeDocumentId) {
                    if (workItem.workItemId == this.transcriptWorkItemId) {
                        //the merged document
                        if (this.permissions == null) return false;
                        return (this.permissions.canMergeTranscript || this.permissions.canCheckTranscript);
                    }
                    //a turn document
                    if (workItem.completedByUserAccountId == this.userAccountId) return true;
                    if (this.permissions == null) return false;
                    return (this.permissions.canMergeTranscript || this.permissions.canCheckTranscript || this.permissions.canCheckTranscriptTurns);
                }
                return false;
            },
            topscribeViewUrl: function (workItem) {
                if (this.topscribeViewBaseUrl && workItem.topscribeDocumentId) {
                    var topscribeViewUrl = this.topscribeViewBaseUrl;
                    if (!topscribeViewUrl.endsWith('/')) topscribeViewUrl += '/';
                    topscribeViewUrl += workItem.topscribeDocumentId;
                    return topscribeViewUrl;
                }
                return null;
            },
            endWorkActionAvailable: function (workItem) {
                if (this.readonly) return false;
                if (workItem.completedDateTimeUTC) return false;
                if (!workItem.inProgressByUserAccountId) return false;
                if (workItem.workItemId == this.transcriptWorkItemId) {
                    if (this.permissions.canMergeTranscript) return true;
                    return (workItem.inProgressByUserAccountId == this.userAccountId);
                } else {
                    if (this.turnsReadonly) return false;
                    if (this.permissions.canAlwaysEndWork) return true;
                    return (workItem.inProgressByUserAccountId == this.userAccountId);
                }
            },
            endWork: function (workItem, confirmAction = false) {
                var data = {
                    action: 'endWork'
                };
                if (confirmAction) data.confirmAction = confirmAction;
                this.currentErrors = [];
                var confirmRequired = false;
                var confirmationMessage = null;
                axios
                    .put(this.apiUrl + '/workitem/' + workItem.workItemId + '/status', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        if (ex.response && ex.response.status == 409) {
                            confirmRequired = true;
                            confirmationMessage = ex.response.data;
                        } else {
                            this.currentErrors = this.handleAxiosException(ex);
                        }
                    })
                    .finally(() => {
                        if (confirmRequired) {
                            this.$bvModal.msgBoxConfirm([this.$createElement('div', { domProps: { innerHTML: confirmationMessage } })], {
                                title: 'Confirm Action',
                                size: 'md',
                                okVariant: 'danger',
                                okTitle: 'YES',
                                cancelTitle: 'NO',
                                centered: true
                            }).then(value => {
                                if (value) {
                                    this.endWork(workItem, true);
                                }
                            });
                        }
                        else if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            completeTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.inProgressByUserAccountId == this.userAccountId) return true;
                if (this.myActiveTurnNumber != null) return false;
                if (turnTranscriptWorkItem.turnNumber != this.nextAvailableTurnNumber) return false;
                if (turnTranscriptWorkItem.subTurnNumber != this.nextAvailableSubTurnNumber) return false;
                if (this.hasTopscribeDocuments && (turnTranscriptWorkItem.topscribeDocumentId == null)) return false;
                if (this.permissions.canCompleteAllTurns) return true;
                return false;
            },
            markEmptyTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.inProgressSinceDateTimeUTC != null) {
                    if (turnTranscriptWorkItem.inProgressByUserAccountId != this.userAccountId)
                        return false;
                }
                return (this.permissions.canCompleteAllTurns || this.permissions.canStartWork);
            },
            revertEmptyTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (!turnTranscriptWorkItem.isEmptyTurn) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                if (turnTranscriptWorkItem.mergedDateTimeUTC != null) return false;
                if (this.transcriptWorkItem != null && this.transcriptWorkItem.mergedDateTimeUTC != null) return false;
                if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return true;
                if (this.permissions.canCompleteAllTurns) return true;
                return false;
            },
            splitTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly || !this.permissions.canManageTurns) return false;
                if (turnTranscriptWorkItem.subTurnNumber) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                return true;
            },
            undoSplitTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly || !this.permissions.canManageTurns) return false;
                if (turnTranscriptWorkItem.subTurnNumber == null) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                var otherSubTurns = this.turnTranscriptWorkItems.filter(t => t.turnNumber == turnTranscriptWorkItem.turnNumber && t.subTurnNumber != turnTranscriptWorkItem.subTurnNumber);
                if (otherSubTurns.length == 0) return false;
                var canUndoSplitTurn = true;
                otherSubTurns.forEach(t => {
                    if (t.completedDateTimeUTC != null) canUndoSplitTurn = false;
                    if (t.inProgressSinceDateTimeUTC != null) canUndoSplitTurn = false;
                });
                return canUndoSplitTurn;
            },
            qcCheckTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.permissions == null || !this.permissions.canCheckTranscriptTurns) return false;
                if (turnTranscriptWorkItem.isEmptyTurn) return false;
                if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return false;
                if (turnTranscriptWorkItem.qcInProgressByUserAccountId != this.userAccountId) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                //if (turnTranscriptWorkItem.mergedDateTimeUTC != null) return false;
                return true;
            },
            openCompleteTurnTranscriptWorkItemDialog: function (turnTranscriptWorkItem) {
                var start = moment(turnTranscriptWorkItem.startTime);
                var end = moment(turnTranscriptWorkItem.endTime);
                var difference = end.diff(start, 'seconds');
                turnTranscriptWorkItem.numberOfAudioSeconds = difference;
                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.currentErrors = [];
                this.completeTurnTranscriptWorkItemDialogOpen = true;
                this.$nextTick(() => {
                    this.$refs.completeAttachments.reload(true);
                });
            },
            openMarkEmptyTurnTranscriptWorkItemDialog: function (turnTranscriptWorkItem) {
                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.currentErrors = [];
                this.markEmptyTurnTranscriptWorkItemDialogOpen = true;
            },
            openQcCheckDialog: function (turnTranscriptWorkItem) {
                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.loadQcCheckTurnTranscriptWorkItem(turnTranscriptWorkItem)
                    .then((success) => {
                        if (success) {
                            this.qcCheckDialogData.dialogOpen = true;
                            this.$nextTick(() => {
                                this.$refs.completeQCAttachments.reload(true);
                            });
                        }
                    });

                //this.currentErrors = [];
                //this.qcCheckDialogOpen = true;
            },
            completeTurnTranscriptWorkItemDialogShown: function () {
                if (this.currentTurnTranscriptWorkItem.topscribeDocumentId) this.$refs.comments.focus();
                else this.$refs.numberOfWords.focus();
                this.$emit('modalOpen', true);

            },
            markEmptyTurnTranscriptWorkItemDialogShown: function () {

                this.$refs.comments.focus();
                this.$emit('modalOpen', true);

            },
            completeTurnTranscriptWorkItem: function () {
                var data = {
                    ...this.currentTurnTranscriptWorkItem
                };
                this.currentErrors = [];
                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.currentTurnTranscriptWorkItem.workItemId + '/complete', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.completeTurnTranscriptWorkItemDialogOpen = false;
                        }
                    });
            },
            markEmptyTurnTranscriptWorkItem: function () {
                var data = {
                    ...this.currentTurnTranscriptWorkItem
                };
                this.currentErrors = [];
                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.currentTurnTranscriptWorkItem.workItemId + '/markempty', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                        if (ex.response && ex.response.status == 409) {
                            this.currentTurnTranscriptWorkItem.confirmAction = true;
                        }
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.markEmptyTurnTranscriptWorkItemDialogOpen = false;
                        }
                    });
            },
            revertMarkEmptyTurnTranscriptWorkItem: function (turnTranscript) {
                var data = {
                    ...turnTranscript
                };
                this.currentErrors = [];
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to undo the mark empty action on turn number " + turnTranscript.turnNumber + "?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Undo Mark Turn Empty',
                        size: 'md',
                        okVariant: 'success',
                        okTitle: 'Yes, Undo Mark Turn Empty',
                        cancelTitle: 'No, Keep Turn Marked Empty',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            axios
                                .put(this.apiUrl + '/workitem/turntranscript/' + turnTranscript.workItemId + '/undomarkempty', JSON.stringify(data),
                                    {
                                        headers: {
                                            'Content-Type': 'application/json'
                                        }
                                    })
                                .then(() => {

                                    this.$bvToast.toast('Turn reverted successfully', {
                                        title: 'Empty Turn Reverted',
                                        variant: 'success',
                                        appendToast: true
                                    });
                                    this.reload();

                                })
                                .catch(ex => {
                                    this.currentErrors = this.handleAxiosException(ex);
                                });
                        }
                    });
            },
            loadQcCheckTurnTranscriptWorkItem: function (turnTranscriptWorkItem) {
                return axios
                    .get(this.$urlHelper.getApiUrl('workitem', 'turntranscript', [turnTranscriptWorkItem.workItemId, 'loadqcdata']))
                    .then((result) => {
                        this.qcCheckDialogData.qcSheet = result.data;
                        return true;
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                        return false;
                    });
            },
            updateQCCheckTurnTranscriptWorkItem: function () {
                var data = this.qcCheckDialogData.qcSheet;
                this.currentErrors = [];
                axios
                    .post(this.apiUrl + '/workitem/turntranscript/' + this.currentTurnTranscriptWorkItem.workItemId + '/saveqcdata', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.qcCheckDialogData.dialogOpen = false;
                        }
                    });
            },
            editTotalsActionAvailable: function (turnTranscriptWorkItem) {
                if (turnTranscriptWorkItem.isEmptyTurn) return false;
                if (this.hasTopscribeDocuments) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC == null) return false;
                if (turnTranscriptWorkItem.mergedDateTimeUTC != null) return false;
                if (this.transcriptWorkItem != null && this.transcriptWorkItem.mergedDateTimeUTC != null) return false;
                if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return true;
                return this.permissions.canModifyPostCompletion;
            },
            editFirstLastWordActionAvailable: function (turnTranscriptWorkItem) {
                if (turnTranscriptWorkItem.mergedDateTimeUTC != null) return false;
                if (this.transcriptWorkItem != null && this.transcriptWorkItem.mergedDateTimeUTC != null) return false;
                if (turnTranscriptWorkItem.inProgressByUserAccountId == this.userAccountId) return true;
                if (turnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) return true;
                if (this.transcriptWorkItem != null && this.transcriptWorkItem.completedDateTimeUTC != null) return false;
                return false;
            },
            openEditTotalsDialog: function (turnTranscriptWorkItem) {
                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.currentErrors = [];
                this.turnEditTitle = 'Update Turn Totals';
                this.openFirstAndLastWordEditModal = false;
                this.editTranscriptTotalsDialogOpen = true;
            },
            openAttachmentDialog: function (turnTranscriptWorkItem) {

                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.currentErrors = [];
                this.editAttachmentClassifications = [];
                if (this.currentTurnTranscriptWorkItem.completedByUserAccountId == this.userAccountId) {
                    if (this.turnTranscriptAttachmentClassificationOption) this.editAttachmentClassifications.push(this.turnTranscriptAttachmentClassificationOption);
                } else if (this.currentTurnTranscriptWorkItem.qcByUserAccountId == this.userAccountId) {
                    if (this.correctedTurnTranscriptAttachmentClassificationOption) this.editAttachmentClassifications.push(this.correctedTurnTranscriptAttachmentClassificationOption);
                }
                this.editAttachmentDialogOpen = true;
                this.$nextTick(() => {
                    this.$refs.attachments.reload(true);
                })
            },
            openSplitTurnDialog: function (turnTranscriptWorkItem) {

                this.currentTurnTranscriptWorkItem = JSON.parse(JSON.stringify(turnTranscriptWorkItem));
                this.currentErrors = [];
                this.splitTime1 = null;
                this.splitTime2 = null;
                this.addNewTurnBefore = false;
                this.splitTurnDialogOpen = true;
            },

            splitTurn: function () {

                this.showSplitTurnAlertMessage = false;
                this.alertMessage = '';

                var turnStartTime = this.parseDateTime(this.currentTurnTranscriptWorkItem.startTime, null, false, false);
                var turnEndTime = this.parseDateTime(this.currentTurnTranscriptWorkItem.endTime, null, false, false);

                // adding the date part to the split Times for compare below
                var splitTime1 = this.setDateComponent(this.parseDateTime(this.splitTime1, 'HH:mm:ss', false, false), turnStartTime);
                var splitTime2 = this.splitTime2 ? this.setDateComponent(this.parseDateTime(this.splitTime2, 'HH:mm:ss', false, false), turnStartTime) : null;

                if (turnStartTime > splitTime1 || turnEndTime < splitTime1) {
                    this.splitTurnAlertMessage = 'Split time 1 outside turn start and end times';
                    this.showSplitTurnAlertMessage = true;
                    return false;
                }
                if (splitTime2 && (turnStartTime > splitTime2 || turnEndTime < splitTime2)) {
                    this.splitTurnAlertMessage = 'Split time 2 outside turn start and end times';
                    this.showSplitTurnAlertMessage = true;
                    return false;
                }

                if (this.splitTime2 && (this.splitTime1 > this.splitTime2)) {
                    this.showSplitTurnAlertMessage = true;
                    this.splitTurnAlertMessage = 'Split time 1 must be smaller than split time 2';
                    return false;
                }

                var data = {
                    //this is to make sure that we pass datetime strings without timezone conversion to the server - if we let JSON.stringify to the conversion it will convert everything to UTC
                    splitTime1: this.formatDateTime(splitTime1, 'YYYY-MM-DDTHH:mm:ss', false, false),
                    splitTime2: splitTime2 ? this.formatDateTime(splitTime2, 'YYYY-MM-DDTHH:mm:ss', false, false) : null,
                    addNewTurnBefore: this.addNewTurnBefore
                };

                this.currentErrors = [];
                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.currentTurnTranscriptWorkItem.workItemId + '/split', JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.splitTurnDialogOpen = false;
                        }
                    });
            },

            undoSplitTurn: function (turnTranscriptWorkItem) {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to undo the split turn action on turn number " + turnTranscriptWorkItem.turnNumber + "?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Undo Split Turn',
                        size: 'md',
                        okVariant: 'success',
                        okTitle: 'Yes, Undo Split Turn',
                        cancelTitle: 'No, Keep Sub Turns',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            axios
                                .get(this.$urlHelper.getApiUrl('workitem', 'turntranscript', [turnTranscriptWorkItem.workItemId, 'undosplit']))
                                .then(() => {
                                    this.reload();
                                })
                                .catch(ex => {
                                    var currentErrors = this.handleAxiosException(ex);
                                    this.showErrorsToast(currentErrors);
                                });
                        }
                    });
            },

            updateTranscriptTotals: function () {
                var data = {
                    numberOfWords: this.currentTurnTranscriptWorkItem.numberOfWords,
                    numberOfAudioSeconds: this.currentTurnTranscriptWorkItem.numberOfAudioSeconds,
                    numberOfPages: this.currentTurnTranscriptWorkItem.numberOfPages,
                    firstWords: this.currentTurnTranscriptWorkItem.firstWords,
                    lastWords: this.currentTurnTranscriptWorkItem.lastWords,
                    lastWordsRequired: (this.currentTurnTranscriptWorkItem.completedByUserAccountId != null),
                };

                var selectedMethod = this.openFirstAndLastWordEditModal ? '/editfirstlastwords' : '/updatetotals';

                this.currentErrors = [];
                axios
                    .put(this.apiUrl + '/workitem/turntranscript/' + this.currentTurnTranscriptWorkItem.workItemId + selectedMethod, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.editTranscriptTotalsDialogOpen = false;
                            this.openFirstAndLastWordEditModal = false;
                        }
                    });
            },
            updateAttachment: function () {
                this.editAttachmentDialogOpen = false;
            },
            mergeTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (!turnTranscriptWorkItem.topscribeDocumentId) return false;
                if (!turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.mergedDateTimeUTC) return false;
                if (!this.permissions.canMergeTranscript) return false;
                if (!this.hasTopscribeDocuments || !this.transcriptWorkItem) return false;
                if (this.transcriptWorkItem.completedDateTimeUTC) return false;
                if (!this.transcriptWorkItem.inProgressSinceDateTimeUTC) return true;
                return (this.transcriptWorkItem.inProgressByUserAccountId == this.userAccountId);
            },
            mergeTurn: function (turnTranscriptWorkItem) {
                var url = this.apiUrl + '/topscribedocument/' + turnTranscriptWorkItem.topscribeDocumentId + '/merge';
                console.log(url);
                axios
                    .get(this.apiUrl + '/topscribedocument/' + turnTranscriptWorkItem.topscribeDocumentId + '/merge')
                    .then(() => {
                        this.$bvToast.toast('Turn merged successfully', {
                            title: 'Turn Merged',
                            variant: 'success',
                            appendToast: true
                        });
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },
            removeTurnActionAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.inProgressSinceDateTimeUTC) return false;
                if (turnTranscriptWorkItem.turnNumber != this.lastTurnNumber) return false;
                if (turnTranscriptWorkItem.subTurnNumber != this.lastSubTurnNumber) return false;
                if ((turnTranscriptWorkItem.turnNumber == 1) && ((turnTranscriptWorkItem.subTurnNumber == null) || (turnTranscriptWorkItem.subTurnNumber == 1))) return false;
                return this.permissions.canManageTurns;
            },
            removeTurnTranscriptWorkItem: function (workItem) {
                this.currentErrors = [];
                axios
                    .delete(this.apiUrl + '/workitem/turntranscript/' + workItem.workItemId)
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            mergeAllAvailableTurns: function () {
                axios
                    .get(this.apiUrl + '/topscribedocument/' + this.transcriptWorkItem.topscribeDocumentId + '/merge/all')
                    .then(() => {
                        this.$bvToast.toast('Turns merged successfully', {
                            title: 'Turns Merged',
                            variant: 'success',
                            appendToast: true
                        });
                        this.reload();
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        if (currentErrors.length != 0) {
                            this.showErrorsToast(currentErrors);
                        }
                    });
            },

            createPreAssignmentAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.inProgressSinceDateTimeUTC) return false;
                if (turnTranscriptWorkItem.preAssignedUserAccountId) return false;
                return this.permissions.canManageTurns;
            },
            showCreatePreAssignment: function (turnTranscriptWorkItem) {
                this.selectedTurn = turnTranscriptWorkItem;
                this.createPreAssignmentDialogOpen = true;
            },

            createPreAssignment: function () {
                axios
                    .post(this.apiUrl + '/workitem/createturnpreassignment/' + this.selectedTurn.workItemId + '/' + this.selectedPreAssignedUser)
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                        else {
                            this.createPreAssignmentDialogOpen = false;
                            this.selectedPreAssignedUser = null;
                            this.selectedTurn = null;
                        }
                    });
            },

            clearPreAssignmentAvailable: function (turnTranscriptWorkItem) {
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.completedDateTimeUTC) return false;
                if (turnTranscriptWorkItem.inProgressSinceDateTimeUTC) return false;
                if (!turnTranscriptWorkItem.preAssignedUserAccountId) return false;
                return this.permissions.canManageTurns;
            },
            clearPreAssignment: function (turnTranscriptWorkItem) {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to clear the pre-assigned user?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Clear Pre-Assignment',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'Yes, Clear',

                        cancelTitle: 'No, Do Not Clear',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            axios
                                .post(this.apiUrl + '/workitem/clearturnpreassignment/' + turnTranscriptWorkItem.workItemId)
                                .then(() => {
                                    this.reload();
                                })
                                .catch(ex => {
                                    this.currentErrors = this.handleAxiosException(ex);
                                })
                                .finally(() => {
                                    if (this.currentErrors.length != 0) {
                                        this.showErrorsToast(this.currentErrors);
                                    }
                                });
                        }
                    });
            },

            showClearUserPreAssignment: function () {
                this.clearUserPreAssignmentDialogOpen = true;
            },
            showSelectTurnAttachments: function (forQC) {
                this.qcMode = forQC;

                var downloadedTurnTranscriptFileIds = [];
                if (forQC) {
                    downloadedTurnTranscriptFileIds = this.getDownloadedQCTurnTranscriptFileIds();
                }
                else
                    downloadedTurnTranscriptFileIds = this.getDownloadedTurnTranscriptFileIds();


                if (downloadedTurnTranscriptFileIds.length > 0) {
                    if (forQC)
                        this.previousQCSelectedKeys =
                            this.turnTranscriptWorkItems
                                .filter(t => {
                                    return !this.isUuidNullOrEmpty(t.turnTranscriptFileId) ? downloadedTurnTranscriptFileIds.includes(t.turnTranscriptFileId) : false;
                                })
                                .map(t => t.workItemId);
                    else
                        this.previousSelectedKeys =
                            this.turnTranscriptWorkItems
                            .filter(t => {
                                    return !this.isUuidNullOrEmpty(t.correctedTurnTranscriptFileId) ?
                                        downloadedTurnTranscriptFileIds.includes(t.correctedTurnTranscriptFileId) :
                                        !this.isUuidNullOrEmpty(t.turnTranscriptFileId) ? downloadedTurnTranscriptFileIds.includes(t.turnTranscriptFileId) : false;
                                })
                                .map(t => t.workItemId);
                } else {
                    if (forQC)
                        this.previousQCSelectedKeys = [];
                    else
                        this.previousSelectedKeys = [];
                }

                this.openSelectTurnAttachmentsModal = true;
            },
            clearUserPreAssignment: function () {
                axios
                    .post(this.apiUrl + '/workitem/clearuserturnpreassignment/' + this.transcriptWorkItemId + '/' + this.selectedPreAssignedUser)
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                        else {
                            this.clearUserPreAssignmentDialogOpen = false;
                            this.selectedPreAssignedUser = null;
                        }
                    });

            },

            createQcPreAssignmentAvailable: function (turnTranscriptWorkItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.readonly || this.turnsReadonly) return false;
                if (turnTranscriptWorkItem.qcInProgressSinceDateTimeUTC) return false;
                if (turnTranscriptWorkItem.qcDateTimeUTC) return false;
                if (turnTranscriptWorkItem.preAssignedQCUserAccountId) return false;
                if (this.qcPreAssignOptions.length == 0) return false;
                return this.permissions.canManageTurns;
            },
            clearQcPreAssignmentAvailable: function (turnTranscriptWorkItem) {
                if (this.hasTopscribeDocuments) return false;
                if (this.readonly || this.turnsReadonly) return false;
                if (!this.transcriptWorkItem) return false;
                if (this.transcriptWorkItem.mergedDateTimeUTC) return false;
                if ((this.preAssignedQcUserList == null) || (this.preAssignedQcUserList.length == 0)) return false;
                if (turnTranscriptWorkItem.qcInProgressSinceDateTimeUTC) return false;
                if (turnTranscriptWorkItem.qcDateTimeUTC) return false;
                if (turnTranscriptWorkItem.preAssignedQCUserAccountId == null) return false;
                return this.permissions.canManageTurns;
            },
            showQcPreAssignment: function (turnTranscriptWorkItem) {
                this.selectedTurn = turnTranscriptWorkItem;
                this.createQcPreAssignmentDialogOpen = true;
            },
            showClearQcPreAssignment: function (turnTranscriptWorkItem) {
                this.selectedTurn = turnTranscriptWorkItem;
                this.clearQcPreAssignmentDialogOpen = true;
            },
            createQcPreAssignment: function () {
                axios
                    .post(this.apiUrl + '/workitem/createqcpreassignment/' + this.selectedTurn.workItemId + '/' + this.selectedQcPreAssignedUser)
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                        else {
                            this.createQcPreAssignmentDialogOpen = false;
                            this.selectedQcPreAssignedUser = null;
                            this.selectedTurn = null;
                        }
                    });
            },
            clearQcPreAssignment: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to clear the pre-assigned QC user?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Clear QC Pre-Assignment',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'Yes, Clear',

                        cancelTitle: 'No, Do Not Clear',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            axios
                                .post(this.apiUrl + '/workitem/clearqcpreassignment/' + this.selectedTurn.workItemId)
                                .then(() => {
                                    this.reload();
                                })
                                .catch(ex => {
                                    this.currentErrors = this.handleAxiosException(ex);
                                })
                                .finally(() => {
                                    if (this.currentErrors.length != 0) {
                                        this.showErrorsToast(this.currentErrors);
                                    } else {
                                        this.clearQcPreAssignmentDialogOpen = false;
                                        this.selectedTurn = null;
                                    }
                                });
                        }
                    });
            },

            openProductionDocumentsDialogActionAvailable: function () {
                return this.permissions.canMergeTranscript || this.permissions.canCheckTranscript;
            },
            openProductionDocumentsDialog: function () {

                if (this.transcriptWorkItem.mergedTurns == '' || this.transcriptWorkItem.mergedTurns == "0")
                {
                    this.$bvToast.toast('At least one turn has to be merged in order to produce a document', {
                        title: 'Docuement Production',
                        variant: 'warning',
                        appendToast: true
                    });
                } else {
                    this.productionDocumentsDialogOpen = true; 
                }
            },

            openFulfillmentsDialogActionAvailable: function () {
                return this.permissions.canDeliver &&
                    (this.transcriptWorkItem.ownStatus == this.$enum.WorkItemStatus.ReadyToBeDelivered) &&
                    this.transcriptWorkItem.hasOrdersWithExternalSources;
            },
            openFulfillmentsDialog: function () {
                this.fulfillmentsDialogOpen = true;
            },

            iconScale: function (scale) {
                return 1.2 * scale;
            }
        },
        mounted: function () {
            this.reload();
        }
    }
</script>

<style>
    .file-already-downloaded {
        color: darkgreen;
        font-style: italic;
    }

    .blank-audio-segment-bg {
        background-color: #FFC0CB;
    }

    .turn-bg {
        background-color: #eee;
    }
</style>